import { createContext, useContext, ReactNode, useEffect, useRef, useCallback, useState } from 'react'

interface WebSocketMessage {
  type: string
  [key: string]: any
}

interface WebSocketContextValue {
  sendMessage: (message: any) => void
  isConnected: boolean
  subscribe: (callback: (message: WebSocketMessage) => void) => () => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

interface WebSocketProviderProps {
  children: ReactNode
  url: string
}

export function WebSocketProvider({ children, url }: WebSocketProviderProps) {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const [isConnected, setIsConnected] = useState(false)
  const subscribersRef = useRef<Set<(message: WebSocketMessage) => void>>(new Set())
  const isInitializedRef = useRef(false)

  const connect = useCallback(() => {
    if (isInitializedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] Already connected, skipping duplicate connection')
      return
    }

    try {
      console.log('[WebSocket] Initializing single connection to:', url)
      const ws = new WebSocket(url)
      wsRef.current = ws
      isInitializedRef.current = true

      ws.onopen = () => {
        console.log('[WebSocket] Connection established')
        reconnectAttemptsRef.current = 0
        setIsConnected(true)
      }

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          // Broadcast to all subscribers
          subscribersRef.current.forEach(callback => {
            try {
              callback(message)
            } catch (error) {
              console.error('[WebSocket] Subscriber callback error:', error)
            }
          })
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error)
        }
      }

      ws.onclose = () => {
        console.log('[WebSocket] Connection closed')
        setIsConnected(false)
        wsRef.current = null

        // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
        const backoffDelay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
        reconnectAttemptsRef.current++

        console.log(`[WebSocket] Reconnecting in ${backoffDelay}ms...`)
        reconnectTimeoutRef.current = setTimeout(() => {
          connect()
        }, backoffDelay)
      }

      ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error)
      }
    } catch (error) {
      console.error('[WebSocket] Failed to create connection:', error)
    }
  }, [url])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (wsRef.current) {
      console.log('[WebSocket] Disconnecting')
      wsRef.current.close()
      wsRef.current = null
    }
    isInitializedRef.current = false
    reconnectAttemptsRef.current = 0
    setIsConnected(false)
  }, [])

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
    } else {
      console.warn('[WebSocket] Not connected, cannot send message')
    }
  }, [])

  const subscribe = useCallback((callback: (message: WebSocketMessage) => void) => {
    subscribersRef.current.add(callback)
    console.log('[WebSocket] Subscriber added, total subscribers:', subscribersRef.current.size)

    return () => {
      subscribersRef.current.delete(callback)
      console.log('[WebSocket] Subscriber removed, total subscribers:', subscribersRef.current.size)
    }
  }, [])

  // Initialize connection on mount
  useEffect(() => {
    connect()
    return () => {
      disconnect()
    }
  }, [url])

  const value: WebSocketContextValue = {
    sendMessage,
    isConnected,
    subscribe
  }

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider')
  }
  return context
}
